import React, { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { X, Check, ZoomIn, ZoomOut, RectangleHorizontal, Square, RectangleVertical } from 'lucide-react';

const PRESETS = [
  { label: 'Product (4:5)', ratio: 4 / 5, icon: RectangleVertical },
  { label: 'Square (1:1)', ratio: 1, icon: Square },
  { label: 'Wide (16:9)', ratio: 16 / 9, icon: RectangleHorizontal },
];

/**
 * Take a source image + a crop rectangle (react-easy-crop pixel output) and
 * return a JPEG data-URL of the cropped region, downscaled to at most 1600px
 * on the longest side (keeps upload well under our 4 MB limit).
 */
async function cropToDataUrl(imageSrc, pixelCrop) {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const MAX = 1600;
        const scale = Math.min(1, MAX / Math.max(pixelCrop.width, pixelCrop.height));
        const w = Math.round(pixelCrop.width * scale);
        const h = Math.round(pixelCrop.height * scale);
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img,
          pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height,
          0, 0, w, h,
        );
        resolve(canvas.toDataURL('image/jpeg', 0.9));
      } catch (e) {
        reject(e);
      }
    };
    img.onerror = reject;
    img.src = imageSrc;
  });
}

export default function ImageCropperModal({ src, defaultRatio = 4 / 5, onCancel, onConfirm, testId = 'cropper' }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [ratio, setRatio] = useState(defaultRatio);
  const [pixelCrop, setPixelCrop] = useState(null);
  const [saving, setSaving] = useState(false);

  const onCropComplete = useCallback((_, croppedPixels) => setPixelCrop(croppedPixels), []);

  const confirm = async () => {
    if (!pixelCrop) return;
    setSaving(true);
    try {
      const dataUrl = await cropToDataUrl(src, pixelCrop);
      onConfirm(dataUrl);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-3" style={{ background: 'rgba(75,24,56,0.6)' }} data-testid={testId}>
      <div className="w-full max-w-3xl rounded-2xl overflow-hidden flex flex-col max-h-[92vh]" style={{ background: '#FBF8F1', border: '3px solid var(--heritage-plum)' }}>
        <div className="px-5 py-3 border-b flex items-center justify-between" style={{ borderColor: 'rgba(75,24,56,0.15)' }}>
          <div>
            <div className="h-eyebrow">Crop image</div>
            <div className="h-serif text-lg mt-0.5" style={{ color: 'var(--heritage-plum)' }}>Position, zoom, then confirm</div>
          </div>
          <button onClick={onCancel} aria-label="Cancel" className="p-1" style={{ color: 'var(--heritage-plum)' }} data-testid={`${testId}-cancel`}><X size={20} /></button>
        </div>

        <div className="relative bg-neutral-800" style={{ height: '55vh', minHeight: 320 }}>
          <Cropper
            image={src}
            crop={crop}
            zoom={zoom}
            aspect={ratio}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
            objectFit="contain"
            restrictPosition={false}
          />
        </div>

        <div className="px-5 py-3 border-t space-y-3" style={{ borderColor: 'rgba(75,24,56,0.15)' }}>
          {/* Aspect presets */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="h-eyebrow text-[10px]">Aspect</span>
            {PRESETS.map((p) => {
              const Icon = p.icon;
              const sel = Math.abs(ratio - p.ratio) < 0.01;
              return (
                <button
                  key={p.label}
                  type="button"
                  onClick={() => setRatio(p.ratio)}
                  className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs h-serif transition"
                  style={{
                    background: sel ? 'var(--heritage-plum)' : 'transparent',
                    color: sel ? '#F6F0E4' : 'var(--heritage-plum)',
                    border: '1px solid var(--heritage-plum)',
                  }}
                  data-testid={`${testId}-ratio-${p.ratio.toFixed(2)}`}
                >
                  <Icon size={12} /> {p.label}
                </button>
              );
            })}
          </div>
          {/* Zoom slider */}
          <div className="flex items-center gap-3">
            <span className="h-eyebrow text-[10px] flex items-center gap-1"><ZoomOut size={11} /> Zoom</span>
            <input
              type="range"
              min={1}
              max={5}
              step={0.05}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="flex-1 accent-[var(--heritage-plum)]"
              data-testid={`${testId}-zoom`}
            />
            <ZoomIn size={12} style={{ color: 'var(--heritage-plum)' }} />
          </div>
        </div>

        <div className="px-5 py-3 border-t flex items-center justify-end gap-2" style={{ borderColor: 'rgba(75,24,56,0.15)' }}>
          <button onClick={onCancel} className="h-btn-outline text-xs">Cancel</button>
          <button onClick={confirm} disabled={saving || !pixelCrop} className="h-btn text-xs" data-testid={`${testId}-confirm`}>
            <Check size={12} className="mr-1" /> {saving ? 'Cropping…' : 'Use this crop'}
          </button>
        </div>
      </div>
    </div>
  );
}
